import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr.length <= 10 ? dateStr + 'T00:00:00' : dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

function PublicChangeOrderApproval() {
  const { token } = useParams();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null); // 'approved' | 'declined'
  const [submitting, setSubmitting] = useState(false);
  const [signature, setSignature] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/public/change-order/${token}/data`)
      .then(res => {
        if (!res.ok) throw new Error('Change order not found');
        return res.json();
      })
      .then(data => {
        setCo(data);
        if (data.status === 'Approved') setAction('approved');
        else if (data.status === 'Rejected') setAction('declined');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [token]);

  const handleApprove = async () => {
    if (!signature.trim()) {
      alert('Please type your full name to sign and approve.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/change-order/${token}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: signature.trim() }),
      });
      const data = await res.json();
      if (res.ok) setAction('approved');
      else alert(data.error || 'Failed to approve');
    } catch {
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/public/change-order/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason }),
      });
      const data = await res.json();
      if (res.ok) setAction('declined');
      else alert(data.error || 'Failed to decline');
    } catch {
      alert('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f3f4f6' }}>
        <div style={{ color: '#6b7280', fontSize: '20px' }}>Loading change order...</div>
      </div>
    );
  }

  if (error || !co) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f3f4f6' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', maxWidth: '400px' }}>
          <h2 style={{ color: '#dc2626', margin: '0 0 8px' }}>Not Found</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>{error || 'This link may have expired.'}</p>
        </div>
      </div>
    );
  }

  const alreadyActioned = co.status === 'Approved' || co.status === 'Rejected' || action === 'approved' || action === 'declined';

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '24px 16px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ background: '#1e40af', color: 'white', borderRadius: '12px 12px 0 0', padding: '24px 28px' }}>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>{co.company_name}</div>
          <h1 style={{ margin: '6px 0 0', fontSize: '24px' }}>Change Order {co.co_number}</h1>
          <div style={{ fontSize: '14px', opacity: 0.85, marginTop: '4px' }}>
            Job {co.job_number}{co.property_address ? ` — ${co.property_address}` : ''}
          </div>
        </div>

        {/* Status banners */}
        {action === 'approved' && (
          <div style={{ background: '#dcfce7', borderLeft: '4px solid #16a34a', padding: '16px 20px' }}>
            <strong style={{ color: '#15803d' }}>Approved</strong>
            <span style={{ color: '#166534', marginLeft: '8px', fontSize: '14px' }}>
              Thank you{co.client_signature ? `, ${co.client_signature}` : ''}. This change order has been approved.
            </span>
          </div>
        )}
        {action === 'declined' && (
          <div style={{ background: '#fee2e2', borderLeft: '4px solid #dc2626', padding: '16px 20px' }}>
            <strong style={{ color: '#b91c1c' }}>Declined</strong>
            <span style={{ color: '#991b1b', marginLeft: '8px', fontSize: '14px' }}>This change order has been declined.</span>
          </div>
        )}

        {/* Body */}
        <div style={{ background: 'white', padding: '28px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: '18px', color: '#1f2937' }}>{co.title}</h2>
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>Dated {formatDate(co.date_created)}</div>
          </div>

          {co.description && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '12px', textTransform: 'uppercase', color: '#6b7280' }}>Scope of Additional Work</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{co.description}</p>
            </div>
          )}
          {co.reason && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '12px', textTransform: 'uppercase', color: '#6b7280' }}>Reason for Change</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{co.reason}</p>
            </div>
          )}

          {/* Contract math */}
          <div style={{ background: '#eff6ff', border: '2px solid #1e40af', borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '14px' }}>
              <span>Current contract value</span>
              <span>{formatCurrency(co.contract_before)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '14px', color: '#1e40af', fontWeight: 600 }}>
              <span>This change order</span>
              <span>+{formatCurrency(co.amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: '4px', borderTop: '2px solid #1e40af', fontSize: '17px', fontWeight: 700 }}>
              <span>New contract total</span>
              <span>{formatCurrency(co.contract_after)}</span>
            </div>
          </div>

          {/* Approve / Decline */}
          {!alreadyActioned && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px', marginTop: '20px' }}>
              {!showDecline ? (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1f2937' }}>Approve this change order</h3>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    Type your full name to sign *
                  </label>
                  <input
                    type="text"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Your full name"
                    style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box', marginBottom: '8px' }}
                  />
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px' }}>
                    By typing your name and clicking Approve, you authorize this change order and the updated contract total above.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      onClick={handleApprove}
                      disabled={submitting}
                      style={{ flex: 1, minWidth: '180px', padding: '14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {submitting ? 'Submitting...' : 'Approve Change Order'}
                    </button>
                    <button
                      onClick={() => setShowDecline(true)}
                      disabled={submitting}
                      style={{ padding: '14px 24px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}
                    >
                      Decline
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1f2937' }}>Decline this change order</h3>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={3}
                    placeholder="Optional — let us know why, or what you'd like to discuss."
                    style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '12px' }}
                  />
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleDecline}
                      disabled={submitting}
                      style={{ padding: '12px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {submitting ? 'Submitting...' : 'Confirm Decline'}
                    </button>
                    <button
                      onClick={() => setShowDecline(false)}
                      disabled={submitting}
                      style={{ padding: '12px 24px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '15px', cursor: 'pointer' }}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ background: 'white', borderRadius: '0 0 12px 12px', padding: '16px 28px', textAlign: 'center', color: '#9ca3af', fontSize: '12px', borderTop: '1px solid #f3f4f6' }}>
          {co.company_name} &middot; {co.company_phone}
        </div>
      </div>
    </div>
  );
}

export default PublicChangeOrderApproval;
